import { useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Emery } from "@/components/ui/Calla";
import { ExtractedFieldRow } from "@/components/upload/ExtractedFieldRow";
import { PolicyCoveragesCard } from "@/components/upload/PolicyCoveragesCard";
import {
  mapExtractedPolicyToForm,
  countUnconfirmedFields,
  type MappedFormData,
} from "@/lib/decPageMapping";
import type {
  ExtractedPolicy,
  FieldMetadata,
  ExtractedFieldMetadata,
} from "@/types/extraction";

interface ExtractionReviewProps {
  extracted: ExtractedPolicy;
  onContinue: (formData: MappedFormData, metadata: FieldMetadata) => void;
  onStartOver: () => void;
}

const EMERY_REVIEW =
  "Here's what I pulled from your policy. Please confirm anything flagged in yellow or red, then we'll add the few things your policy doesn't show.";

export function ExtractionReview({
  extracted,
  onContinue,
  onStartOver,
}: ExtractionReviewProps) {
  const initialMapping = useMemo(
    () => mapExtractedPolicyToForm(extracted),
    [extracted],
  );

  const [formData, setFormData] = useState<MappedFormData>(initialMapping.formData);
  const [metadata, setMetadata] = useState<FieldMetadata>(initialMapping.metadata);

  const unconfirmedCount = countUnconfirmedFields(metadata);
  const canContinue = unconfirmedCount === 0;

  const updateMetadata = (path: string, update: Partial<ExtractedFieldMetadata>) => {
    setMetadata((prev) => ({
      ...prev,
      [path]: { ...prev[path], ...update },
    }));
  };

  const acknowledgeField = (path: string) => {
    updateMetadata(path, { acknowledged: true });
  };

  const editField = (path: string, newValue: string | number) => {
    updateMetadata(path, {
      source: "user_edited",
      acknowledged: true,
    });
    // Apply the edit to formData by dotted path.
    setFormData((prev) => setByDotPath(prev, path, newValue));
  };

  const acknowledgeAllInSection = (paths: string[]) => {
    setMetadata((prev) => {
      const next = { ...prev };
      for (const p of paths) {
        if (next[p] && (next[p].confidence === "medium" || next[p].confidence === "low")) {
          next[p] = { ...next[p], acknowledged: true };
        }
      }
      return next;
    });
  };

  const getField = (path: string): ExtractedFieldMetadata | undefined => metadata[path];

  return (
    <div className="max-w-xl mx-auto">
      <Emery message={EMERY_REVIEW} />

      {/* Sticky progress header */}
      <div
        className="sticky mb-6 p-4"
        style={{
          top: 16,
          zIndex: 10,
          background: "white",
          border: "1px solid var(--border-default, #E5E7EB)",
          borderRadius: 16,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold" style={{ color: "var(--color-navy)", fontSize: 15 }}>
              Review what we found
            </div>
            <div className="text-sm mt-0.5" style={{ color: "var(--text-secondary)" }}>
              {unconfirmedCount === 0
                ? "Everything confirmed. Ready to continue."
                : `${unconfirmedCount} item${unconfirmedCount === 1 ? "" : "s"} need${
                    unconfirmedCount === 1 ? "s" : ""
                  } your review`}
            </div>
          </div>
        </div>
      </div>

      {/* Section: Policy */}
      <ReviewSection title="Policy">
        <FieldRow
          label="Carrier"
          path="step1.carrierName"
          currentValue={
            formData.step1?.carrierName === "Other"
              ? formData.step1?.carrierNameOther ?? null
              : formData.step1?.carrierName ?? null
          }
          metadata={getField("step1.carrierName")}
          onAcknowledge={() => acknowledgeField("step1.carrierName")}
          onEdit={(v) => editField("step1.carrierName", v)}
        />
        <FieldRow
          label="Policy number"
          path="step1.policyNumber"
          currentValue={formData.step1?.policyNumber ?? null}
          metadata={getField("step1.policyNumber")}
          onAcknowledge={() => acknowledgeField("step1.policyNumber")}
          onEdit={(v) => editField("step1.policyNumber", v)}
        />
        <ExtractedDateRow
          label="Policy effective date"
          extracted={extracted.policy.effectiveDate}
        />
        <ExtractedDateRow
          label="Policy expires"
          extracted={extracted.policy.expirationDate}
        />
      </ReviewSection>

      {/* Section: About You */}
      <ReviewSection title="About you">
        <FieldRow
          label="Full legal name"
          path="step2.fullLegalName"
          currentValue={formData.step2?.fullLegalName ?? null}
          metadata={getField("step2.fullLegalName")}
          onAcknowledge={() => acknowledgeField("step2.fullLegalName")}
          onEdit={(v) => editField("step2.fullLegalName", v)}
        />
        <FieldRow
          label="Date of birth"
          path="step2.dateOfBirth"
          currentValue={formData.step2?.dateOfBirth ?? null}
          metadata={getField("step2.dateOfBirth")}
          onAcknowledge={() => acknowledgeField("step2.dateOfBirth")}
          onEdit={(v) => editField("step2.dateOfBirth", v)}
        />
        <FieldRow
          label="Mailing address"
          path="step2.mailingAddressStreet"
          currentValue={formatAddress(formData.step2 ?? {})}
          metadata={getField("step2.mailingAddressStreet")}
          onAcknowledge={() => {
            // Acknowledging the address row marks all address sub-fields as
            // acknowledged, since they share one extraction source.
            acknowledgeAllInSection([
              "step2.mailingAddressStreet",
              "step2.mailingAddressUnit",
              "step2.mailingAddressCity",
              "step2.mailingAddressState",
              "step2.mailingAddressZip",
            ]);
          }}
          onEdit={(v) => editField("step2.mailingAddressStreet", v)}
        />
      </ReviewSection>

      {/* Section: Vehicles (one card per vehicle) */}
      {extracted.vehicles.length > 0 && (
        <ReviewSection title={extracted.vehicles.length > 1 ? "Vehicles" : "Vehicle"}>
          {extracted.vehicles.map((vehicle, i) => {
            const pathPrefix = i === 0 ? "step3" : `step3.additionalVehicles.${i - 1}`;
            const vehicleData =
              i === 0
                ? formData.step3
                : formData.step3?.additionalVehicles?.[i - 1];
            return (
              <VehicleReviewCard
                key={i}
                index={i}
                pathPrefix={pathPrefix}
                data={vehicleData}
                extracted={vehicle}
                getField={getField}
                onAcknowledge={acknowledgeField}
                onEdit={editField}
              />
            );
          })}
        </ReviewSection>
      )}

      {/* Section: Other drivers */}
      {extracted.namedInsureds.length > 1 && (
        <ReviewSection title="Other drivers on the policy">
          {extracted.namedInsureds
            .filter((_, i) => {
              const primary =
                extracted.namedInsureds.find((n) => n.isPrimary) ??
                extracted.namedInsureds[0];
              return extracted.namedInsureds[i] !== primary;
            })
            .map((_driver, i) => (
              <div
                key={i}
                style={{
                  border: "1px solid var(--border-subtle, #F3F4F6)",
                  borderRadius: 12,
                  marginBottom: 12,
                }}
              >
                <FieldRow
                  label="Full legal name"
                  path={`step5.drivers.${i}.fullLegalName`}
                  currentValue={
                    formData.step5?.drivers?.[i]?.fullLegalName ?? null
                  }
                  metadata={getField(`step5.drivers.${i}.fullLegalName`)}
                  onAcknowledge={() =>
                    acknowledgeField(`step5.drivers.${i}.fullLegalName`)
                  }
                  onEdit={(v) =>
                    editField(`step5.drivers.${i}.fullLegalName`, v)
                  }
                />
                <FieldRow
                  label="Date of birth"
                  path={`step5.drivers.${i}.dateOfBirth`}
                  currentValue={
                    formData.step5?.drivers?.[i]?.dateOfBirth ?? null
                  }
                  metadata={getField(`step5.drivers.${i}.dateOfBirth`)}
                  onAcknowledge={() =>
                    acknowledgeField(`step5.drivers.${i}.dateOfBirth`)
                  }
                  onEdit={(v) => editField(`step5.drivers.${i}.dateOfBirth`, v)}
                />
              </div>
            ))}
        </ReviewSection>
      )}

      {/* Section: Coverages (read-only, no form counterpart) */}
      <ReviewSection title="Coverages on this policy">
        <div style={{ padding: 16, background: "white" }}>
          <PolicyCoveragesCard
            coverages={extracted.coverages}
            discountsApplied={extracted.discountsApplied}
          />
        </div>
      </ReviewSection>

      {/* Section: Premium */}
      <ReviewSection title="Premium">
        <FieldRow
          label="Current premium"
          path="step6.currentPremiumAmount"
          currentValue={formData.step6?.currentPremiumAmount ?? null}
          displayValue={
            formData.step6?.currentPremiumAmount != null
              ? formatCurrency(formData.step6.currentPremiumAmount)
              : undefined
          }
          metadata={getField("step6.currentPremiumAmount")}
          onAcknowledge={() => acknowledgeField("step6.currentPremiumAmount")}
          onEdit={(v) => editField("step6.currentPremiumAmount", parseFloat(String(v)))}
          editType="number"
        />
        <FieldRow
          label="Payment frequency"
          path="step6.currentPremiumFrequency"
          currentValue={formData.step6?.currentPremiumFrequency ?? null}
          displayValue={
            formData.step6?.currentPremiumFrequency
              ? paymentFrequencyLabel(formData.step6.currentPremiumFrequency)
              : undefined
          }
          metadata={getField("step6.currentPremiumFrequency")}
          onAcknowledge={() => acknowledgeField("step6.currentPremiumFrequency")}
          onEdit={(v) => editField("step6.currentPremiumFrequency", v)}
        />
        <FieldRow
          label="Autopay enrolled"
          path="step6.hasAutopay"
          currentValue={formData.step6?.hasAutopay === undefined ? null : String(formData.step6?.hasAutopay)}
          displayValue={
            formData.step6?.hasAutopay === undefined
              ? undefined
              : formData.step6.hasAutopay
                ? "Yes"
                : "No"
          }
          metadata={getField("step6.hasAutopay")}
          onAcknowledge={() => acknowledgeField("step6.hasAutopay")}
          onEdit={(v) => editField("step6.hasAutopay", v)}
        />
        <FieldRow
          label="Paperless billing"
          path="step6.hasPaperless"
          currentValue={formData.step6?.hasPaperless === undefined ? null : String(formData.step6?.hasPaperless)}
          displayValue={
            formData.step6?.hasPaperless === undefined
              ? undefined
              : formData.step6.hasPaperless
                ? "Yes"
                : "No"
          }
          metadata={getField("step6.hasPaperless")}
          onAcknowledge={() => acknowledgeField("step6.hasPaperless")}
          onEdit={(v) => editField("step6.hasPaperless", v)}
        />
      </ReviewSection>

      {/* Continue / Start over */}
      <div
        className="sticky mt-8 p-4"
        style={{
          bottom: 16,
          zIndex: 10,
          background: "white",
          border: "1px solid var(--border-default, #E5E7EB)",
          borderRadius: 16,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <button
          type="button"
          disabled={!canContinue}
          onClick={() => onContinue(formData, metadata)}
          className="btn-primary w-full"
          style={{
            opacity: canContinue ? 1 : 0.5,
            cursor: canContinue ? "pointer" : "not-allowed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          Continue to the rest of the form
          <ArrowRight size={18} strokeWidth={2} />
        </button>
        <button
          type="button"
          onClick={onStartOver}
          className="w-full mt-3 text-sm"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--text-secondary)",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Start over with manual entry
        </button>
      </div>
    </div>
  );
}

// ---------------- Helpers and subcomponents ----------------

function ReviewSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-6">
      <h3
        className="mb-2 text-sm font-semibold uppercase tracking-wide"
        style={{ color: "var(--text-secondary)", letterSpacing: 0.6 }}
      >
        {title}
      </h3>
      <div
        style={{
          border: "1px solid var(--border-default, #E5E7EB)",
          borderRadius: 16,
          overflow: "hidden",
          background: "white",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function FieldRow({
  label,
  path: _path,
  currentValue,
  displayValue,
  metadata,
  onAcknowledge,
  onEdit,
  editType,
}: {
  label: string;
  path: string;
  currentValue: string | number | null;
  displayValue?: string;
  metadata: ExtractedFieldMetadata | undefined;
  onAcknowledge: () => void;
  onEdit: (newValue: string) => void;
  editType?: "text" | "number";
}) {
  if (!metadata || metadata.confidence === "not_found") {
    return null;
  }
  return (
    <ExtractedFieldRow
      label={label}
      value={currentValue}
      displayValue={displayValue}
      confidence={metadata.confidence ?? "not_found"}
      sourceQuote={metadata.sourceQuote ?? null}
      acknowledged={metadata.acknowledged ?? false}
      onAcknowledge={onAcknowledge}
      onEdit={onEdit}
      editType={editType}
    />
  );
}

function ExtractedDateRow({
  label,
  extracted,
}: {
  label: string;
  extracted: ExtractedPolicy["policy"]["effectiveDate"];
}) {
  if (extracted.confidence === "not_found" || !extracted.value) return null;
  return (
    <div style={{ padding: "14px 16px", borderBottom: "1px solid #F3F4F6" }}>
      <div
        className="text-xs uppercase tracking-wide font-semibold mb-1"
        style={{ color: "var(--text-secondary)", letterSpacing: 0.4 }}
      >
        {label}
      </div>
      <div style={{ color: "var(--color-navy)" }}>{extracted.value}</div>
    </div>
  );
}

function VehicleReviewCard({
  index,
  pathPrefix,
  data,
  extracted,
  getField,
  onAcknowledge,
  onEdit,
}: {
  index: number;
  pathPrefix: string;
  data: Partial<{ vehicleYear?: number; vehicleMake?: string; vehicleModel?: string; vehicleLienHolder?: string; annualMileage?: number; primaryVehicleUse?: string }> | undefined;
  extracted: ExtractedPolicy["vehicles"][number];
  getField: (path: string) => ExtractedFieldMetadata | undefined;
  onAcknowledge: (path: string) => void;
  onEdit: (path: string, value: string | number) => void;
}) {
  const title =
    data?.vehicleYear && data?.vehicleMake && data?.vehicleModel
      ? `${data.vehicleYear} ${data.vehicleMake} ${data.vehicleModel}`
      : `Vehicle ${index + 1}`;

  return (
    <div
      style={{
        borderBottom: "1px solid var(--border-subtle, #F3F4F6)",
      }}
    >
      <div
        style={{
          padding: "14px 16px",
          background: "var(--bg-muted, #F9FAFB)",
          borderBottom: "1px solid var(--border-subtle, #F3F4F6)",
          fontWeight: 600,
          color: "var(--color-navy)",
          fontSize: 15,
        }}
      >
        {title}
      </div>

      <FieldRow
        label="Year"
        path={`${pathPrefix}.vehicleYear`}
        currentValue={data?.vehicleYear ?? null}
        metadata={getField(`${pathPrefix}.vehicleYear`)}
        onAcknowledge={() => onAcknowledge(`${pathPrefix}.vehicleYear`)}
        onEdit={(v) => onEdit(`${pathPrefix}.vehicleYear`, parseInt(v))}
        editType="number"
      />
      <FieldRow
        label="Make"
        path={`${pathPrefix}.vehicleMake`}
        currentValue={data?.vehicleMake ?? null}
        metadata={getField(`${pathPrefix}.vehicleMake`)}
        onAcknowledge={() => onAcknowledge(`${pathPrefix}.vehicleMake`)}
        onEdit={(v) => onEdit(`${pathPrefix}.vehicleMake`, v)}
      />
      <FieldRow
        label="Model"
        path={`${pathPrefix}.vehicleModel`}
        currentValue={data?.vehicleModel ?? null}
        metadata={getField(`${pathPrefix}.vehicleModel`)}
        onAcknowledge={() => onAcknowledge(`${pathPrefix}.vehicleModel`)}
        onEdit={(v) => onEdit(`${pathPrefix}.vehicleModel`, v)}
      />
      {extracted.vin.value && extracted.vin.confidence !== "not_found" && (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #F3F4F6" }}>
          <div
            className="text-xs uppercase tracking-wide font-semibold mb-1"
            style={{ color: "var(--text-secondary)", letterSpacing: 0.4 }}
          >
            VIN
          </div>
          <div
            style={{
              color: "var(--color-navy)",
              fontFamily: "ui-monospace, Menlo, monospace",
              fontSize: 14,
            }}
          >
            {extracted.vin.value}
          </div>
        </div>
      )}
      <FieldRow
        label="Lien holder"
        path={`${pathPrefix}.vehicleLienHolder`}
        currentValue={data?.vehicleLienHolder ?? null}
        metadata={getField(`${pathPrefix}.vehicleLienHolder`)}
        onAcknowledge={() => onAcknowledge(`${pathPrefix}.vehicleLienHolder`)}
        onEdit={(v) => onEdit(`${pathPrefix}.vehicleLienHolder`, v)}
      />
      <FieldRow
        label="Annual mileage"
        path={`${pathPrefix}.annualMileage`}
        currentValue={data?.annualMileage ?? null}
        metadata={getField(`${pathPrefix}.annualMileage`)}
        onAcknowledge={() => onAcknowledge(`${pathPrefix}.annualMileage`)}
        onEdit={(v) => onEdit(`${pathPrefix}.annualMileage`, parseInt(v))}
        editType="number"
      />
      <FieldRow
        label="Primary use"
        path={`${pathPrefix}.primaryVehicleUse`}
        currentValue={data?.primaryVehicleUse ?? null}
        metadata={getField(`${pathPrefix}.primaryVehicleUse`)}
        onAcknowledge={() => onAcknowledge(`${pathPrefix}.primaryVehicleUse`)}
        onEdit={(v) => onEdit(`${pathPrefix}.primaryVehicleUse`, v)}
      />
    </div>
  );
}

function formatAddress(
  step2: Partial<{
    mailingAddressStreet?: string;
    mailingAddressUnit?: string;
    mailingAddressCity?: string;
    mailingAddressState?: string;
    mailingAddressZip?: string;
  }>,
): string | null {
  if (!step2.mailingAddressStreet) return null;
  const unit = step2.mailingAddressUnit ? ` ${step2.mailingAddressUnit}` : "";
  return `${step2.mailingAddressStreet}${unit}, ${step2.mailingAddressCity ?? ""}, ${step2.mailingAddressState ?? ""} ${step2.mailingAddressZip ?? ""}`.trim();
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(value);
}

function paymentFrequencyLabel(freq: string): string {
  switch (freq) {
    case "monthly":
      return "Monthly";
    case "six_month":
      return "Six-month";
    case "annual":
      return "Annual";
    default:
      return freq;
  }
}

function setByDotPath<T extends object>(
  obj: T,
  path: string,
  value: unknown,
): T {
  const parts = path.split(".");
  const clone = structuredClone(obj);
  let cur: Record<string, unknown> = clone as unknown as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    if (cur[key] === undefined || cur[key] === null) {
      const nextIsIndex = /^\d+$/.test(parts[i + 1]);
      cur[key] = nextIsIndex ? [] : {};
    }
    cur = cur[key] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]] = value;
  return clone;
}
